import { Channel } from 'pusher-js'
import { useEffect, useMemo, useRef } from 'react'

import { EventHandlers } from '@bonhams1793/bonhams-typescript'

import { BonhamsChannel } from '@nx/external/pusher'

import {
  ChannelSlice,
  ChannelState,
} from '../../store/channelState/channelState'
import { buildUserChannelName } from '../buildUserChannelName/buildUserChannelName'

export const useUserChannel = ({
  customerId,
  subscribe,
  unsubscribe,
  addRegistration,
  removeRegistration,
  updateChannelPayload,
}: UseUserChannelProps) => {
  const channelName = useRef<BonhamsChannel>()
  const isRegistered = useRef(false)

  const eventHandlers = useMemo(() => {
    const eventTypes = ['user.outbid'] as const

    const events: EventHandlers = eventTypes.reduce((handlers, eventType) => {
      handlers[eventType] = (payload) => {
        if (channelName.current) {
          updateChannelPayload({
            channelName: channelName.current,
            payload: {
              ...payload,
              type: eventType,
            } as ChannelState['lastPayload'],
          })
        }
      }
      return handlers
    }, {} as EventHandlers)

    return events
  }, [channelName, updateChannelPayload])

  useEffect(() => {
    if (!isRegistered.current && customerId) {
      channelName.current = buildUserChannelName(customerId)

      const registrationCount = addRegistration(channelName.current)
      isRegistered.current = true

      const channel = subscribe(channelName.current, eventHandlers)

      if (registrationCount === 1) {
        channel?.bind('pusher:subscription_error', () => {
          if (channelName.current) {
            removeRegistration(channelName.current)
            isRegistered.current = false
          }
        })
      }
    }
  }, [
    addRegistration,
    eventHandlers,
    removeRegistration,
    subscribe,
    unsubscribe,
    customerId,
  ])

  useEffect(() => {
    if (
      isRegistered.current &&
      channelName.current &&
      !channelName.current.includes(String(customerId))
    ) {
      const registrationCount = removeRegistration(channelName.current)
      isRegistered.current = false

      if (!registrationCount && channelName.current) {
        unsubscribe(channelName.current)
        channelName.current = customerId
          ? buildUserChannelName(customerId)
          : undefined
      }
    }
  }, [customerId])

  useEffect(() => {
    return () => {
      if (isRegistered.current && channelName.current) {
        const registrationCount = removeRegistration(channelName.current)
        isRegistered.current = false

        if (!registrationCount) {
          unsubscribe(channelName.current)
        }
      }
    }
  }, [])

  return {
    isSubscribed: isRegistered.current,
  }
}

export interface UseUserChannelProps {
  customerId?: number
  subscribe: (
    channelName: BonhamsChannel,
    events: EventHandlers,
    onSubscribed?: () => void
  ) => Channel | undefined
  unsubscribe: (channelName: BonhamsChannel) => void
  addRegistration: ChannelSlice['addRegistration']
  removeRegistration: ChannelSlice['removeRegistration']
  updateChannelPayload: ChannelSlice['updateChannelPayload']
}
